import * as React from 'react';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';

/**
 * A Material-UI Autocomplete component wrapped for form usage.
 *
 * @param {object} props - The component props.
 * @param {string} props.name - The name of the input field.
 * @param {string} props.label - The label for the input field.
 * @param {any} props.value - The current value of the autocomplete field.
 * @param {function} props.onChange - Callback function triggered on value change.
 *   Receives an object like { target: { name: string, value: any } }.
 * @param {Array<object|string>} props.options - The options for the autocomplete dropdown.
 *   If objects, they should have a 'label' property or use getOptionLabel.
 * @param {function} [props.getOptionLabel] - Function to determine the string value for a given option.
 * @param {boolean} [props.error=false] - If true, the component is displayed in an error state.
 * @param {string} [props.helperText] - The helper text content.
 * @param {boolean} [props.disabled=false] - If true, the component is disabled.
 * @param {object} [props.textFieldProps] - Additional props to pass to the TextField component.
 * @param {object} [props.autocompleteProps] - Additional props to pass to the Autocomplete component.
 * @returns {JSX.Element} The rendered Autocomplete component.
 */
const AutoCompleteField = ({
  name,
  label,
  value,
  onChange,
  options = [],
  getOptionLabel,
  error = false,
  helperText,
  disabled = false,
  textFieldProps,
  autocompleteProps,
  ...rest // Capture any other props passed down
}) => {
  const handleChange = (event, newValue) => {
    // Simulate a standard event object structure for form handlers
    const simulatedEvent = {
      target: {
        name: name,
        value: newValue,
      },
    };
    onChange(simulatedEvent);
  };

  // Find the option object that matches the current value.
  // Autocomplete needs the object, not just the value string, unless configured otherwise.
  // This assumes `value` holds the identifier/value that needs to be matched against options.
  // If options are just strings, this might need adjustment or rely on Autocomplete's default behavior.
  // If `value` is already the option object, this find is unnecessary.
  // We add a check for `value !== null && value !== undefined` to prevent errors.
  const selectedOption =
    value !== null && value !== undefined
      ? options.find((option) =>
          typeof option === 'object' ? option.value === value : option === value
        ) ?? null
      : null;

  return (
    <Autocomplete
      options={options}
      getOptionLabel={
        getOptionLabel ||
        ((option) =>
          typeof option === 'object' && option !== null
            ? option.label || ''
            : option || '')
      }
      value={selectedOption} // Use the found option object or null
      onChange={handleChange}
      disabled={disabled}
      isOptionEqualToValue={(option, val) => {
        // Customize comparison logic if needed, especially for objects
        if (
          typeof option === 'object' &&
          typeof val === 'object' &&
          option !== null &&
          val !== null
        ) {
          return option.value === val.value;
        }
        return option === val;
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          name={name}
          label={label}
          error={error}
          helperText={helperText}
          {...textFieldProps} // Spread additional TextField props
        />
      )}
      {...autocompleteProps} // Spread additional Autocomplete props
      {...rest} // Spread any other remaining props
    />
  );
};

export default AutoCompleteField;
